import { useState, useMemo, useCallback } from 'react';

// Credit pricing configuration
// Base rate is R4.00/credit for custom amounts
// Fixed packs have discounted rates to incentivize bulk purchases
const PRICING_CONFIG = {
  MINIMUM_PURCHASE: 1,
  MAXIMUM_PURCHASE: 1000,
  BASE_PRICE: 4.00, // Base price for custom amounts
  QUICK_SELECT_OPTIONS: [20, 100, 400, 1000],
  TIERS: [
    { min: 1,    max: 19,   price: 4.00,   tier: 'Custom',     discount: 0 },
    { min: 20,   max: 99,   price: 3.50,   tier: 'Starter',    discount: 12.5 },
    { min: 100,  max: 399,  price: 2.99,   tier: 'Standard',   discount: 25.25 },
    { min: 400,  max: 999,  price: 2.4975, tier: 'Business',   discount: 37.56 },
    { min: 1000, max: 1000, price: 1.999,  tier: 'Enterprise', discount: 50.03 },
  ]
};

// Helper functions
export const formatCurrency = (amount) => {
  return `R${amount.toFixed(2)}`;
};

export const formatSavings = (savings, percentage) => {
  if (savings <= 0) return '';
  return `R${savings.toFixed(2)} (${percentage}% off)`;
};

/**
 * Get pricing tier for a given quantity
 */
const getTier = (quantity) => {
  for (const tier of PRICING_CONFIG.TIERS) {
    if (quantity >= tier.min && quantity <= tier.max) {
      return tier;
    }
  }
  // Default to highest tier for quantities above max
  return PRICING_CONFIG.TIERS[PRICING_CONFIG.TIERS.length - 1];
};

/**
 * Custom hook for credit purchase calculations
 */
export const useCreditCalculator = (initialQuantity = 25) => {
  const [quantity, setQuantityState] = useState(initialQuantity);

  // Validate and set quantity
  const setQuantity = useCallback((value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) {
      setQuantityState(PRICING_CONFIG.MINIMUM_PURCHASE);
      return;
    }
    // Clamp to valid range
    const clampedValue = Math.max(
      PRICING_CONFIG.MINIMUM_PURCHASE,
      Math.min(PRICING_CONFIG.MAXIMUM_PURCHASE, numValue)
    );
    setQuantityState(clampedValue);
  }, []);

  // Calculate pricing details
  const pricingDetails = useMemo(() => {
    const tier = getTier(quantity);
    const totalPrice = quantity * tier.price;
    const standardPrice = quantity * PRICING_CONFIG.BASE_PRICE;
    const savings = standardPrice - totalPrice;
    const savingsPercentage = tier.discount;

    return {
      quantity,
      pricePerCredit: tier.price,
      totalPrice,
      tier: tier.tier,
      savings,
      savingsPercentage,
      standardPrice
    };
  }, [quantity]);

  // Validation
  const validation = useMemo(() => {
    const errors = [];
    const warnings = [];

    if (quantity < PRICING_CONFIG.MINIMUM_PURCHASE) {
      errors.push(`Minimum purchase is ${PRICING_CONFIG.MINIMUM_PURCHASE} credit`);
    }
    if (quantity > PRICING_CONFIG.MAXIMUM_PURCHASE) {
      errors.push(`Maximum purchase is ${PRICING_CONFIG.MAXIMUM_PURCHASE} credits`);
    }

    // Suggest upgrade to next tier
    const currentTier = getTier(quantity);
    const nextTierIndex = PRICING_CONFIG.TIERS.findIndex(t => t.tier === currentTier.tier) + 1;
    if (nextTierIndex < PRICING_CONFIG.TIERS.length) {
      const nextTier = PRICING_CONFIG.TIERS[nextTierIndex];
      const creditsToNextTier = nextTier.min - quantity;
      if (creditsToNextTier <= 5 && creditsToNextTier > 0) {
        warnings.push(`Add ${creditsToNextTier} more credits for ${nextTier.tier} pricing!`);
      }
    }

    return {
      errors,
      warnings,
      canProceed: errors.length === 0 && quantity >= PRICING_CONFIG.MINIMUM_PURCHASE
    };
  }, [quantity]);

  // Quick select handler
  const selectQuickAmount = useCallback((amount) => {
    setQuantityState(amount);
  }, []);

  // Get suggestion for next tier
  const getNextTierSuggestion = useCallback(() => {
    const currentTier = getTier(quantity);
    const currentTierIndex = PRICING_CONFIG.TIERS.findIndex(t => t.tier === currentTier.tier);

    if (currentTierIndex < PRICING_CONFIG.TIERS.length - 1) {
      const nextTier = PRICING_CONFIG.TIERS[currentTierIndex + 1];
      const creditsNeeded = nextTier.min;
      const currentTotal = quantity * currentTier.price;
      const nextTotal = creditsNeeded * nextTier.price;
      const extraCost = nextTotal - currentTotal;
      const extraCredits = creditsNeeded - quantity;

      // Only suggest if within reasonable range
      if (extraCredits <= quantity * 0.5) {
        return {
          credits: creditsNeeded,
          tier: nextTier.tier,
          extraCost: formatCurrency(extraCost),
          extraCredits,
          savings: `Save ${nextTier.discount}%`
        };
      }
    }
    return null;
  }, [quantity]);

  return {
    quantity,
    setQuantity,
    pricingDetails,
    validation,
    selectQuickAmount,
    getNextTierSuggestion,
    config: PRICING_CONFIG
  };
};

// Export credit packs for display purposes
// Prices chosen to provide clear value proposition vs base R4.00/credit rate
export const CREDIT_PACKS = [
  {
    id: 'starter',
    credits: 20,
    price: 70,
    pricePerCredit: 3.50,
    savings: 12.5,
    popular: false,
    description: 'Perfect for trying out'
  },
  {
    id: 'standard',
    credits: 100,
    price: 299,
    pricePerCredit: 2.99,
    savings: 25.25,
    popular: true,
    description: 'Most popular choice'
  },
  {
    id: 'business',
    credits: 400,
    price: 999,
    pricePerCredit: 2.4975,
    savings: 37.56,
    popular: false,
    description: 'Best for teams'
  },
  {
    id: 'enterprise',
    credits: 1000,
    price: 1999,
    pricePerCredit: 1.999,
    savings: 50.03,
    popular: false,
    description: 'Maximum value'
  }
];

export default useCreditCalculator;
